import React, { useEffect, useRef } from 'react'

import { useAppState } from '../appState'
import axios from '../axios'
import { baseURL, ENDPOINTS } from '../constants'
import {
  clearSuspendedBrandIds,
  setBlockedNetworks,
  setSuspendedBrandIds,
} from '../suspendedBrandsRegistry'

/**
 * Loads the current user's own active brand suspensions and blocked networks from the backend and mirrors them into
 * context plus `suspendedBrandsRegistry` for the brand-list filters. Mirrors `UserCjPidsSync`'s
 * shape (same auth-change lifecycle, same stale-response guard) — kept as a separate component since
 * suspension and CJ access are unrelated concepts with independent load states.
 */
const UserSuspendedBrandsSync = () => {
  const [{ auth, userId }, dispatch] = useAppState()
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!auth || userId == null || userId === '') {
      clearSuspendedBrandIds()
      dispatch({ type: 'RESET_SUSPENDED_BRANDS' })
      return
    }

    const id = ++requestIdRef.current
    let cancelled = false

    const run = async () => {
      clearSuspendedBrandIds()
      dispatch({ type: 'SET_SUSPENDED_BRANDS_LOADING' })
      try {
        const response = await axios.get(
          `${baseURL}${ENDPOINTS.suspendedBrandIds}`
        )
        if (cancelled || id !== requestIdRef.current) return

        const suspendedBrandIds = response?.data?.data?.suspendedBrandIds ?? []
        // Additive field — older API responses lack it.
        const blockedNetworks = response?.data?.data?.blockedNetworks ?? []
        setSuspendedBrandIds(suspendedBrandIds)
        setBlockedNetworks(blockedNetworks)
        dispatch({
          type: 'SET_SUSPENDED_BRANDS_SUCCESS',
          suspendedBrandIds,
          blockedNetworks,
        })
      } catch (error) {
        if (cancelled || id !== requestIdRef.current) return
        // Fails open (the backend still filters its lists and rejects writes), so report it: the viewer's
        // blocks go unapplied in the UI until the next load.
        window.Sentry?.captureException(error)
        clearSuspendedBrandIds()
        dispatch({ type: 'SET_SUSPENDED_BRANDS_ERROR' })
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [auth, userId, dispatch])

  return null
}

export default UserSuspendedBrandsSync
